import React, { useContext, useState, createContext, useCallback } from 'react';
import { useQuery } from 'react-query';
import { BASE_API_ENDPOINT, DEFAULT_FLOOR_NUMBER } from '../constants';
import { FloorType } from '../types/floor';
import { RoomObjectType } from '../types/room';
import { getFloorWidthAndHeight, getRandomColor } from '../utils/floor_utils';
import { useTargetContext } from './TargetContext';

interface FloorContextType {
  currentFloor: number;
  currentFloorMap?: FloorType;
  changeCurrentFloor: (floor: number) => void;
  fetchCurrentFloorData: (floor: number) => void;
  totalFloors: number;
  isFetching: boolean;
  setIsFetching: (fetching: boolean) => void;
}

const FloorContext = createContext<FloorContextType>({
  currentFloor: 1,
  currentFloorMap: undefined,
  changeCurrentFloor: () => {},
  fetchCurrentFloorData: () => {},
  totalFloors: 0,
  isFetching: false,
  setIsFetching: () => {},
});

const mapWithRandomRoomColor = (r: RoomObjectType) => {
  return {
    ...r,
    // TODO: should it come from API or be totally removed
    // assign a random color to all rooms
    color: getRandomColor(),
  };
};

const requiredAttributes = [
  'roomId',
  'startX',
  'endX',
  'startY',
  'endY',
  'roomFloor',
  'roomName',
  'roomDescription',
  'roomVisitorLimit',
  'roomLockedState',
  'mapIconSize',
  'imageUrl',
  'mapIconUrl',
  'mapIconStatus',
  'contractAddress',
  'tokenId',
  'isPlaced',
  'roomTypeId',
].join(',');

export function FloorContextProvider({ children }: { children: any }) {
  const { target } = useTargetContext();
  const [isFetchingLayout, setIsFetchingLayout] = useState<boolean>(true);
  const [isFetchingFloor, setIsFetchingFloor] = useState<boolean>(false);
  const [currentFloor, setCurrentFloor] =
    useState<number>(DEFAULT_FLOOR_NUMBER);
  const [layout, setLayout] = useState<FloorType[]>([]);
  const [currentFloorMap, setCurrentFloorMap] = useState<FloorType>();
  const [totalFloors, setTotalFloors] = useState<number>(0);

  const fetchCurrentFloorData = useCallback(
    async (floor: number) => {
      setIsFetchingFloor(true);

      await fetch(
        `${BASE_API_ENDPOINT[target]}/explorer/floor/?floorNumber=${floor}&attributes=${requiredAttributes}`
      )
        .then(async (response) => {
          return await response.json();
        })
        .then((_floorMap) => {
          // @ts-ignore
          setCurrentFloorMap((currentFloorMap) => {
            const rooms = [..._floorMap.rooms];

            for (let i = 0; i < rooms.length; i += 1) {
              const oldRoom = currentFloorMap?.rooms.find(
                (x) => x.roomId === rooms[i].roomId
              );

              if (!oldRoom) {
                rooms[i] = mapWithRandomRoomColor(rooms[i]);
              }

              rooms[i].color = oldRoom?.color;
            }

            return {
              ...currentFloorMap,
              tiles: _floorMap.tiles,
              rooms,
            };
          });

          console.warn('current floor map updated: ' + floor);
          setIsFetchingFloor(false);
        });
    },
    [target]
  );

  const updateCurrentFloorMap = useCallback(
    async (layout: any, newFloor: number) => {
      const currentMap: any = layout.find(
        (f: FloorType) => f.floorNumber === newFloor
      );
      const floorSize = getFloorWidthAndHeight(currentMap);

      setIsFetchingFloor(true);

      await fetch(
        `${BASE_API_ENDPOINT[target]}/explorer/floor/?floorNumber=${newFloor}&attributes=${requiredAttributes}`
      )
        .then(async (response) => {
          return await response.json();
        })
        .then((_floorMap) => {
          setCurrentFloorMap({
            ...currentMap,
            ..._floorMap,
            width: floorSize.width,
            height: floorSize.height,
            rooms: _floorMap.rooms.map(mapWithRandomRoomColor),
          });
          setIsFetchingFloor(false);
        });
    },
    [target]
  );

  const changeCurrentFloor = (f: number) => {
    if (currentFloor !== f) {
      target === 'dev' && console.log('set floor to ' + f);
      setCurrentFloor(f);
      updateCurrentFloorMap(layout, f);
    }
  };

  useQuery(
    'layouts',
    () => {
      setIsFetchingLayout(true);
      return fetch(`${BASE_API_ENDPOINT[target]}/explorer/layouts/`)
        .then(async (response) => {
          return await response.json();
        })
        .then((data) => {
          setLayout(data);
          updateCurrentFloorMap(data, currentFloor);
          setTotalFloors(data.length);

          setIsFetchingLayout(false);
        });
    },
    {
      cacheTime: 1000 * 60 * 5,
      retryOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  return (
    <FloorContext.Provider
      value={{
        currentFloor,
        currentFloorMap,
        changeCurrentFloor,
        fetchCurrentFloorData,
        totalFloors,
        isFetching: isFetchingLayout || isFetchingFloor,
        setIsFetching: setIsFetchingLayout,
      }}
    >
      {children}
    </FloorContext.Provider>
  );
}

export function useFloorContext() {
  const context = useContext(FloorContext);
  if (context === undefined) {
    throw new Error('Floor context must be used within a Provider');
  }
  return context;
}
